import { memo } from 'react';
import { G, Line, Text } from 'react-native-svg';
import { ComboLayout } from './layout';
import { ValueScale } from './scale';
import { ComboCategoryAxis, ComboTheme, ComboValueAxis } from './types';

/** The y of a value on the plot, in svg coordinates. */
export function plotY(layout: ComboLayout, scale: ValueScale, value: number) {
  return layout.plot.top + layout.plot.height - scale.toY(value);
}

const ValueTicks = memo(
  ({
    layout,
    scale,
    axis,
    side,
    width,
    theme,
  }: {
    layout: ComboLayout;
    scale: ValueScale;
    axis: ComboValueAxis;
    side: 'left' | 'right';
    width: number;
    theme: ComboTheme;
  }) => {
    if (!axis.show) return null;
    const f = theme.fontSize;
    const { plot } = layout;
    const x = side === 'left' ? plot.left - 4 : plot.left + plot.width + 4;
    const middle = plot.top + plot.height / 2;
    const titleX = side === 'left' ? f * 0.8 : width - f * 0.8;
    return (
      <G>
        {scale.ticks.map(tick => (
          <Text
            key={tick}
            x={x}
            y={plotY(layout, scale, tick) + f * 0.35}
            fontSize={f}
            fontFamily={theme.fontFamily}
            fill={theme.axisText}
            textAnchor={side === 'left' ? 'end' : 'start'}
          >
            {axis.format(tick)}
          </Text>
        ))}
        {axis.title ? (
          <Text
            x={titleX}
            y={middle}
            fontSize={f}
            fontFamily={theme.fontFamily}
            fill={theme.axisTitle}
            textAnchor="middle"
            transform={`rotate(${
              side === 'left' ? -90 : 90
            } ${titleX} ${middle})`}
          >
            {axis.title}
          </Text>
        ) : null}
      </G>
    );
  },
);

/** Grid lines, the baseline, both value axes and the category labels. */
export const ComboAxes = memo(
  ({
    layout,
    width,
    height,
    xAxis,
    yAxis,
    y2Axis,
    gridLines,
    theme,
  }: {
    layout: ComboLayout;
    width: number;
    height: number;
    xAxis: ComboCategoryAxis;
    yAxis: ComboValueAxis;
    y2Axis?: ComboValueAxis;
    gridLines: boolean;
    theme: ComboTheme;
  }) => {
    const { plot, band, left, right, step, labels, rotate } = layout;
    const f = theme.fontSize;
    const grid = left ?? right;
    const bottom = plot.top + plot.height;
    const baseline = grid ? plotY(layout, grid, Math.max(grid.min, 0)) : bottom;
    return (
      <G>
        {gridLines && grid
          ? grid.ticks.map(tick => (
              <Line
                key={tick}
                x1={plot.left}
                x2={plot.left + plot.width}
                y1={plotY(layout, grid, tick)}
                y2={plotY(layout, grid, tick)}
                stroke={theme.grid}
                strokeWidth={1}
                strokeDasharray="3,3"
              />
            ))
          : null}
        <Line
          x1={plot.left}
          x2={plot.left + plot.width}
          y1={baseline}
          y2={baseline}
          stroke={theme.axisLine}
          strokeWidth={1}
        />
        {left ? (
          <ValueTicks
            layout={layout}
            scale={left}
            axis={yAxis}
            side="left"
            width={width}
            theme={theme}
          />
        ) : null}
        {right && y2Axis ? (
          <ValueTicks
            layout={layout}
            scale={right}
            axis={y2Axis}
            side="right"
            width={width}
            theme={theme}
          />
        ) : null}
        {xAxis.show
          ? labels.map((label, i) => {
              if (i % step !== 0) return null;
              const x = plot.left + band * (i + 0.5);
              const y = bottom + 6;
              if (rotate === 0)
                return (
                  <Text
                    key={i}
                    x={x}
                    y={bottom + f + 3}
                    fontSize={f}
                    fontFamily={theme.fontFamily}
                    fill={theme.axisText}
                    textAnchor="middle"
                  >
                    {label}
                  </Text>
                );
              return (
                <Text
                  key={i}
                  x={x + f * 0.35}
                  y={y}
                  fontSize={f}
                  fontFamily={theme.fontFamily}
                  fill={theme.axisText}
                  textAnchor="end"
                  transform={`rotate(${-rotate} ${x + f * 0.35} ${y})`}
                >
                  {label}
                </Text>
              );
            })
          : null}
        {xAxis.show && xAxis.title ? (
          <Text
            x={plot.left + plot.width / 2}
            y={height - 3}
            fontSize={f}
            fontFamily={theme.fontFamily}
            fill={theme.axisTitle}
            textAnchor="middle"
          >
            {xAxis.title}
          </Text>
        ) : null}
      </G>
    );
  },
);
